/**
 * M7 S6: which `StoryEvent` completes which mission step (plan §2; spec §1 rule 1: existing verbs only).
 *
 * A step names a verb and a target `{ kind, ref }`. An event completes it when the event's type is one the verb
 * runs on (`VERB_EVENTS`) and the event carries the target as one of its keys (`eventKeys`: `station:<id>`,
 * `system:<id>`, `item:<id>`, ...). A target ref of `*` matches any key of that kind. A step that reuses a contract
 * type (`contractType`) also completes on that type's `contractDone`; the job itself is story-owned (no deadline,
 * never in `contracts.active`, spec §2.8 SL-1), so no expiry path reaches it. A courier or delivery step that names
 * an `item` completes only with that item aboard (not in a death pod).
 *
 * Pure and allocation-light; called once per story event, never per frame.
 */
import type { Career } from '../career/types'
import { itemAboard, storyItemOf } from './items'
import type { MissionDef } from './schemas'
import type { StepVerb, StoryEvent } from './types'

export type MissionStep = MissionDef['steps'][number]
export type StoryEventType = StoryEvent['type']

/** The event types each verb runs on (S30 maps them from the game's existing events). */
export const VERB_EVENTS: Readonly<Record<StepVerb, readonly StoryEventType[]>> = {
  scan: ['scanned', 'meterReading'],
  salvage: ['salvaged'],
  recover: ['itemPicked', 'salvaged'],
  deliver: ['delivered', 'docked', 'contractDone'],
  courier: ['docked', 'delivered', 'contractDone'],
  laneRepair: ['laneRepaired', 'contractDone'],
  relayParts: ['delivered', 'contractDone'],
  dock: ['docked'],
  comms: ['commsReply'],
  clearRaiders: ['raidersCleared', 'killed', 'contractDone'],
  reboot: ['rebooted'],
  claim: ['claimMade'],
}

/** The `kind:ref` keys an event satisfies. */
export function eventKeys(event: StoryEvent): string[] {
  switch (event.type) {
    case 'contractDone':
      return [`station:${event.stationId}`, `faction:${event.factionId}`, `contract:${event.contractType}`]
    case 'scanned':
      return [`poi:${event.targetId}`, `${event.targetKind}:${event.targetId}`, `system:${event.systemId}`]
    case 'salvaged': {
      const keys = [`part:${event.partId}`, `component:${event.componentType}`, `system:${event.systemId}`]
      if (event.poiId !== undefined) keys.push(`poi:${event.poiId}`)
      return keys
    }
    case 'docked':
      return [`station:${event.stationId}`, `system:${event.systemId}`]
    case 'crime':
      return [`crime:${event.kind}`, `system:${event.systemId}`]
    case 'killed': {
      const keys = [`faction:${event.factionId}`, `system:${event.systemId}`]
      if (event.role !== undefined) keys.push(`role:${event.role}`)
      if (event.storyShip !== undefined) keys.push(`ship:${event.storyShip}`)
      return keys
    }
    case 'itemPicked':
      return [`item:${event.itemId}`]
    case 'commsReply':
      return [`node:${event.nodeId}`, `reply:${event.nodeId}/${event.replyId}`]
    case 'claimMade':
      return event.correct ? [`tier:${event.tier}`] : []
    case 'onlineDay':
      return []
    case 'laneRepaired':
      return [`ring:${event.ringId}`, `system:${event.systemId}`]
    case 'delivered':
      return [`station:${event.targetId}`, `target:${event.targetId}`, `good:${event.good}`]
    case 'rebooted':
      return event.ok ? [`target:${event.targetId}`, `poi:${event.targetId}`] : []
    case 'raidersCleared': {
      const keys = [`system:${event.systemId}`]
      if (event.siteId !== undefined) keys.push(`site:${event.siteId}`)
      return keys
    }
    case 'logRead':
      return [`log:${event.logId}`]
    case 'meterReading':
      return [`meter:${event.opId}`, `system:${event.systemId}`]
  }
}

function keyMatches(keys: readonly string[], kind: string, ref: string): boolean {
  if (ref === '*') {
    const prefix = `${kind}:`
    return keys.some((k) => k.startsWith(prefix))
  }
  return keys.includes(`${kind}:${ref}`)
}

/** The verbs whose named item has to be aboard when the step completes (it is what gets carried). */
const CARRY_VERBS: ReadonlySet<StepVerb> = new Set(['courier', 'deliver'])

/** True when `event` completes `step` for this career. */
export function stepMatches(career: Career, step: MissionStep, event: StoryEvent): boolean {
  if (!VERB_EVENTS[step.verb].includes(event.type)) return false
  if (event.type === 'contractDone') {
    // Only a step that reuses this contract type runs on its hand-in.
    if (step.contractType === undefined || step.contractType !== event.contractType) return false
  }
  if (!keyMatches(eventKeys(event), step.target.kind, step.target.ref)) {
    const fb = step.fallback
    if (fb === undefined || !keyMatches(eventKeys(event), fb.kind, fb.ref)) return false
  }
  if (step.item !== undefined && CARRY_VERBS.has(step.verb)) {
    const item = storyItemOf(career, step.item)
    if (item === undefined || !itemAboard(item)) return false
  }
  return true
}
